// Constants
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { GridColumnToTaskPropertyMap, SortableColumns } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
import { compareByDisplayName, compareOrderHints, compareStringForDisplay } from "@ms/uno-utilities/lib/local/Comparators";
// Lodash imports
import { isEqual } from "@ms/uno-utilities/lib/local/ObjectUtilities";
/**
 * Sort order enumeration
 */ export var SortOrder = /*#__PURE__*/ function(SortOrder) {
    SortOrder[SortOrder["Ascending"] = 0] = "Ascending";
    SortOrder[SortOrder["Descending"] = 1] = "Descending";
    return SortOrder;
}({});
/**
 * Generates a task sorting comparator based on sort descriptors
 * @param sortDescriptors Array of sort field descriptors
 * @returns Comparison function for tasks
 */ export function generateTasksSortComparator(sortDescriptors) {
    return (a, b)=>{
        for (const descriptor of sortDescriptors){
            let result = 0;
            switch(descriptor.fieldName){
                case "displayName":
                    result = compareByDisplayName(a, b, (task)=>task.displayName);
                    break;
                case "priority":
                    result = compareByPriority(a, b);
                    break;
                case "dueDateTime":
                    result = compareByDueDate(a, b);
                    break;
                case "finishDateTime":
                    result = compareByCompletionDate(a, b);
                    break;
                case "startDateTime":
                    result = compareByStartDate(a, b);
                    break;
                case "status":
                    result = compareStringForDisplay(a.status, b.status);
                    break;
                case "getPersonalViewOrderHint":
                    result = compareByPersonalViewOrderHint(a, b);
                    break;
                case "getPlanViewOrderHint":
                    result = compareByPlanViewOrderHint(a, b);
                    break;
                case "bucketId":
                    result = compareByBucketName(descriptor.taskExternalFieldMethods.getBucketName)(a, b);
                    break;
                case "planId":
                    result = compareByPlanName(descriptor.taskExternalFieldMethods.getPlanName)(a, b);
                    break;
                case "userAssignments":
                    result = compareByFirstAssigneeDisplayName(descriptor.taskExternalFieldMethods.getFirstAssigneeDisplayName)(a, b);
                    break;
                default:
                    result = compareByPersonalViewOrderHint(a, b);
            }
            if (descriptor.sortOrder === 1) {
                result = -result;
            }
            if (result !== 0) {
                return result;
            }
        }
        return 0;
    };
}
export function getDefaultTasksSortComparator_ATMView() {
    return generateTasksSortComparator([
        {
            fieldName: "getPersonalViewOrderHint",
            sortOrder: 0,
            taskExternalFieldMethods: {
                getFirstAssigneeDisplayName: ()=>null,
                getBucketName: ()=>null,
                getPlanName: ()=>null
            }
        }
    ]);
}
export function getDefaultTasksSortComparator_PlanView() {
    return generateTasksSortComparator([
        {
            fieldName: "getPlanViewOrderHint",
            sortOrder: 1,
            taskExternalFieldMethods: {
                getFirstAssigneeDisplayName: ()=>null,
                getBucketName: ()=>null,
                getPlanName: ()=>null
            }
        }
    ]);
}
/**
 * Compares tasks by due date
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByDueDate(a, b) {
    const dateValueAccessor = (task)=>{
        if (!task.dueDateTime?.temporal) {
            return null;
        }
        return task.dueDateTime.temporal;
    };
    return compareByTemporalDate(a, b, dateValueAccessor) || compareByDisplayName(a, b, (task)=>task.displayName);
}
/**
 * Compares tasks by percentage complete
 * @param a First task
 * @param b Second task
 * @returns Comparison result: a number between 1 and -1
 */ export function compareByPersonalViewOrderHint(a, b) {
    return compareOrderHints(a.getPersonalViewOrderHint(TaskGroupingOption.None), b.getPersonalViewOrderHint(TaskGroupingOption.None));
}
/**
 * Compares tasks by plan view order hint
 * @param a First task
 * @param b Second task
 * @returns Comparison result: a number between 1 and -1
 */ export function compareByPlanViewOrderHint(a, b) {
    return compareOrderHints(a.getPlanViewOrderHint(TaskGroupingOption.None), b.getPlanViewOrderHint(TaskGroupingOption.None));
}
/**
 * Comparator get method for alphabetical sort on first assignee display name
 */ export function compareByFirstAssigneeDisplayName(getFirstAssigneeDisplayName) {
    return (a, b)=>compareStringForDisplay(getFirstAssigneeDisplayName(a), getFirstAssigneeDisplayName(b));
}
/**
 * Comparator get method for alphabetical sort on bucket name
 */ export function compareByBucketName(getBucketName) {
    return (a, b)=>compareStringForDisplay(getBucketName(a), getBucketName(b));
}
/**
 * Comparator get method for alphabetical sort on plan name
 */ export function compareByPlanName(getPlanName) {
    return (a, b)=>compareStringForDisplay(getPlanName(a), getPlanName(b));
}
/**
 * Compares tasks for custom sort order
 * @param a First task
 * @param b Second task
 * @param customSortOrder Array of task IDs in desired order
 * @returns Comparison result
 */ export function compareForCustomSort(a, b, customSortOrder) {
    const indexA = customSortOrder.indexOf(a.id);
    const indexB = customSortOrder.indexOf(b.id);
    if (indexA === -1 && indexB === -1) {
        return compareByDisplayName(a, b, (task)=>task.displayName);
    }
    if (indexA === -1) {
        return 1;
    }
    if (indexB === -1) {
        return -1;
    }
    return indexA - indexB;
}
/**
 * Gets the completion date for a task
 * @param task Task to get completion date from
 * @returns Task completion date or null
 */ function getTaskCompletionDate(task) {
    if (task?.isComplete() && task.finishDateTime) {
        return task.finishDateTime.temporal;
    }
    return task.finishDateTime?.temporal || null;
}
/**
 * Compares two tasks by their completion date
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByCompletionDate(a, b) {
    const dateA = getTaskCompletionDate(a);
    const dateB = getTaskCompletionDate(b);
    if (!dateA && !dateB) {
        return 0;
    }
    if (!dateA) {
        return 1;
    }
    if (!dateB) {
        return -1;
    }
    if (dateA instanceof Temporal.ZonedDateTime && dateB instanceof Temporal.ZonedDateTime) {
        return Temporal.ZonedDateTime.compare(dateA, dateB);
    }
    if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
        return Temporal.PlainDate.compare(dateA, dateB);
    }
    // Mixed types - convert to comparable format
    const instantA = dateA instanceof Temporal.ZonedDateTime ? dateA.toInstant() : dateA.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
    const instantB = dateB instanceof Temporal.ZonedDateTime ? dateB.toInstant() : dateB.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
    return Temporal.Instant.compare(instantA, instantB);
}
/**
 * Compares tasks by creation date
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByCreatedDate(a, b) {
    const dateA = a.creationInfo?.createdDateTime;
    const dateB = b.creationInfo?.createdDateTime;
    if (!dateA && !dateB) {
        return 0;
    }
    if (!dateA) {
        return 1;
    }
    if (!dateB) {
        return -1;
    }
    return Temporal.Instant.compare(dateA, dateB);
}
/**
 * Compares tasks by start date
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByStartDate(a, b) {
    const dateValueAccessor = (task)=>task?.startDateTime?.temporal ?? null;
    return compareByTemporalDate(a, b, dateValueAccessor);
}
/**
 * Compares tasks by priority
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByPriority(a, b) {
    // Lower priority number = higher priority (0 is highest)
    const result = b.priority - a.priority;
    return result !== 0 ? result : compareByDisplayName(a, b, (task)=>task.displayName);
}
/**
 * Compares tasks by progress state (completion status)
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByProgress(a, b) {
    const progressA = a.isComplete() ? 1 : 0;
    const progressB = b.isComplete() ? 1 : 0;
    const result = progressA - progressB;
    return result !== 0 ? result : compareByDisplayName(a, b, (task)=>task.displayName);
}
/**
 * Compares tasks by display name
 * @param a First task
 * @param b Second task
 */ export function compareByName(a, b) {
    return compareByDisplayName(a, b, (task)=>task.displayName);
}
/**
 * Generic temporal date comparison function
 * @param a First item
 * @param b Second item
 * @param dateValueAccessor Function to extract date from item
 * @returns Comparison result
 */ function compareByTemporalDate(a, b, dateValueAccessor) {
    const dateA = dateValueAccessor(a);
    const dateB = dateValueAccessor(b);
    if (!dateA && !dateB) {
        return 0;
    }
    if (!dateA) {
        return 1;
    }
    if (!dateB) {
        return -1;
    }
    if (dateA instanceof Temporal.ZonedDateTime && dateB instanceof Temporal.ZonedDateTime) {
        return Temporal.ZonedDateTime.compare(dateA, dateB);
    }
    if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
        return Temporal.PlainDate.compare(dateA, dateB);
    }
    // Mixed types - convert to comparable format
    const instantA = dateA instanceof Temporal.ZonedDateTime ? dateA.toInstant() : dateA.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
    const instantB = dateB instanceof Temporal.ZonedDateTime ? dateB.toInstant() : dateB.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
    return Temporal.Instant.compare(instantA, instantB);
}
/**
 * Compares two multi-field sort descriptors for equality
 * @param a First sort descriptor
 * @param b Second sort descriptor
 * @returns True if equal, false otherwise
 */ export function areMultiFieldSortDescriptorsEqual(a, b) {
    return isEqual(a, b);
}
/**
 * Gets the task sort accessor function based on view and sort state
 * @param viewId The current view ID
 * @param sortState The current sort state
 * @param taskExternalFieldMethods The external field methods (to get bucket/plan/assignee names from IDs)
 * @returns A function that compares two tasks based on the current sort state
 */ export const getTaskSortAccessor = (viewId, sortState, taskExternalFieldMethods)=>{
    let defaultSort;
    switch(viewId.name){
        case ViewName.PlanPage:
            defaultSort = getDefaultTasksSortComparator_PlanView();
            break;
        case ViewName.MyTasksPage:
            defaultSort = getDefaultSortForMyTasksView(viewId.partitionKey);
            break;
        default:
            defaultSort = (a, b)=>0;
            break;
    }
    if (!sortState) {
        return defaultSort;
    }
    const { columnId, isAscending } = sortState;
    const typedColumnId = columnId;
    if (SortableColumns.includes(typedColumnId)) {
        const fieldName = GridColumnToTaskPropertyMap[typedColumnId];
        const sortOrder = isAscending ? 0 : 1;
        if (fieldName) {
            return generateTasksSortComparator([
                {
                    fieldName,
                    sortOrder,
                    taskExternalFieldMethods
                }
            ]);
        }
    }
    return defaultSort;
};
export const getDefaultSortForMyTasksView = (partitionKey)=>{
    switch(partitionKey){
        case MyTasksViewPivotType.All:
            return getDefaultTasksSortComparator_PlannerAllUserGridView();
        case MyTasksViewPivotType.PrivateTasks:
            return getDefaultTasksSortComparator_PlanView();
        default:
            return getDefaultTasksSortComparator_ATMView();
    }
};
const getDefaultTasksSortComparator_PlannerAllUserGridView = ()=>{
    return generateTasksSortComparator([
        {
            fieldName: "getPersonalViewOrderHint",
            sortOrder: 0,
            taskExternalFieldMethods: {
                getFirstAssigneeDisplayName: ()=>null,
                getBucketName: ()=>null,
                getPlanName: ()=>null
            }
        }
    ]);
};
